import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import MainContext from "../../context/mainContext";
import { useTranslation } from "react-i18next";
import useService from "../../hooks/useService";
import summaryService from "../../services/summary.service";
import styles from "./Summary.module.scss";
import SpinnerArea from "../../components/SpinnerArea";
import { Box, Button, Paper } from "@mui/material";
import SummaryAccordion from "./SummaryAccordion";
import { ISummaryHeaderCreationDeleteModel } from "../../models/summary.model";
import BackButton from "../../components/BackButton";
import workflowService from "../../services/workflow.service";
import StatusComponent from "../publications/StatusComponent";
import ObservationButton from '../../components/ObservationButton/index';

const Summary: React.FC = () => {
  const { ticketNumber } = useParams();
  const { setBanner, service } = useContext(MainContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [summaryHeader, loading, , reload] = useService(() =>
    summaryService.getHeader(ticketNumber!)
  );

  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      if (
        summaryHeader?.canViewWorkflow ||
        panel === "generalInformation" ||
        panel === "deleteApproval"
      )
        setExpanded(isExpanded ? panel : false);
    };

  const [tryingToContinue, setTryingToContinue] = useState(false);

  useEffect(() => {
    setBanner("rocky-peak-sunset.png");
  });

  useEffect(() => {
    return reload();
  }, [ticketNumber]);

  const getAccordionNames = (): Array<
    keyof ISummaryHeaderCreationDeleteModel
  > => {
    if (summaryHeader) {
      const objectKeys = Object.keys(summaryHeader.headers) as Array<
        keyof ISummaryHeaderCreationDeleteModel
      >;
      
      return objectKeys;
    }

    return [];
  };

  const continueToPublication = () => {
    if (summaryHeader?.canViewWorkflow) {
      if (
        summaryHeader.headers.draft &&
        summaryHeader.headers.reviewers &&
        !summaryHeader.headers.finalDraft
      ) {
        service(
          () =>
            workflowService.draftForFeedbackTryContinue(
              summaryHeader.publicationId!
            ),
          {
            callback: () =>
              navigate(`/workflow/${summaryHeader.ticketNumber}`, {
                relative: "path",
              }),
            loader: setTryingToContinue,
          }
        );
      } else if (
        summaryHeader.headers.finalDraft &&
        !summaryHeader.headers.finalVersion
      ) {
        service(
          () =>
            workflowService.finalDraftTryContinue(summaryHeader.publicationId!),
          {
            callback: () =>
              navigate(`/workflow/${summaryHeader.ticketNumber}`, {
                relative: "path",
              }),
            loader: setTryingToContinue,
          }
        );
      } else if (
        summaryHeader?.headers.finalVersion &&
        summaryHeader.headers.finalVersionApproval
      ) {
        service(
          () =>
            workflowService.finalVersionTryContinue(
              summaryHeader.publicationId!
            ),
          {
            callback: () =>
              navigate(`/workflow/${summaryHeader.ticketNumber}`, {
                relative: "path",
              }),
            loader: setTryingToContinue,
          }
        );
      } else {
        navigate(`/workflow/${summaryHeader?.ticketNumber}`, {
          relative: "path",
        });
      }
    }
  };

  return (
    <SpinnerArea loading={loading || tryingToContinue}>
      <Box className={styles.summaryBox}>
        <div className={styles.summaryTitle}>
          <h2>{t("publication")} <strong>{t("publicationHUB")}</strong></h2>
        </div>
        <Paper className={styles.main}>
          {summaryHeader && (
            <div className={styles.summaryContent}>
              <BackButton text="back" />
              <div className={styles.publicationContainer}>
                <div className={styles.publicationNumber}>
                  <p>
                    <strong>{t("publicationTicketNumber")}: </strong>
                    {summaryHeader.ticketNumber}
                  </p>
                </div>
                <div className={styles.publicationStatus}>
                  <StatusComponent statusCode={summaryHeader.status} />
                </div>
                <ObservationButton ticketNumber={summaryHeader.ticketNumber} to={`../observationsByPublications/${summaryHeader?.ticketNumber}`} canViewObservations={summaryHeader.canViewEditObservations.canViewObservations} openNewTab={false} />
              </div>
              <div className={styles.summaryAccordion}>
                {getAccordionNames().map((x) => (
                  <SummaryAccordion
                    key={`accordion_item_${x}`}
                    summaryHeader={summaryHeader}
                    accordionName={x}
                    expanded={expanded === x}
                    handleChange={handleChange}
                  />
                ))}
              </div>
              {summaryHeader.canViewWorkflow && (
                <div className={styles.summaryActions}>
                  <Button variant="contained" onClick={continueToPublication}>
                    {t("continueToThePublication")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Paper>
      </Box>
    </SpinnerArea>
  );
};

export default Summary;
